import { useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  TextButton,
  SearchBar,
  PrimaryButton,
  IconButton,
  PaginatedLoadingTable,
  OutlineButton,
  Badge,
} from '@artprov/design-system'
import { ButtonCargaNomina } from '../nomina/components/buttonCargaNomina.component'
import {
  createDeclaracionNomina as createDeclaracionNominaApi,
  getPlantilaBase as getPlantilaBaseApi,
  getResumenNomina as getResumenNominaApi,
  validateNomina as validateNominaApi,
  getDetalleNomina as getDetalleNominaApi,
  updateValuesItemDetalleNomina as updateValuesItemDetalleNominaApi,
  getNominaConErrores as getNominaConErroresApi,
  updateDeclaracionNomina as updateDeclaracionNominaApi,
  bajaDeclaracion,
} from '../../services'
import { detalleNominaAdapter, resumenNominaAdapter } from '../../services'
import { columnasDetalleNomina } from './columnasDetalleNomina'
import { useAlert } from '@common/components/alert/use-alert.hook'
import ModalValidarDatos from './components/ModalValidarDatos'
import ModalCorreccionErrorItem from './components/correccionErrores/ModalCorreccionErrorItem'
import ModalSuccessAfterValidarDatos from './components/ModalSuccessAfterValidarDatos'
import { adaptarPeriodo } from '../../common/components/InputMonthYear/date.utils'
import { useLoader } from '../../common/components/loader/use-loader.hook'
import { tiposDocumento } from './tiposDocumento'
import BackButton from './components/backButton'
import ModalUpdateDetalleNomina from './components/ModalUpdateDetalleNomina'
import ModalEliminarNomina from './components/ModalEliminarNomina'
import IndicadorOs from '../productor/components/IndicatorOs'
import { useContratoStore } from '../../common/store/contrato.store'

export default function Nomina() {
  const DEBOUNCE_TIME = 700
  const QUANTITY_LIMIT_ERROR = 2
  const { razonSocial, cuit } = useContratoStore()
  const fileRef = useRef(null)
  const { periodo, id } = useParams()
  const navigate = useNavigate()
  const alert = useAlert()
  const location = useLocation()
  const loader = useLoader()
  const [detalleNomina, setDetalleNomina] = useState([])
  const [disabledBtnRevisarNomina, setDisabledBtnRevisarNomina] = useState(true)
  const [cantidadErrores, setCantidadErrores] = useState(0)
  const [openModalValidarDatos, setOpenModalValidarDatos] = useState(false)
  const [openModalCorreccionErrorItem, setOpenModalCorreccionErrorItem] = useState(false)
  const [selectedRow, setSelectedRow] = useState([])
  const [itemDetalleNomina, setItemDetalleNomina] = useState({})
  const [dataItem, setDataItem] = useState({})

  const [empleados, setEmpleados] = useState(0)
  const [salarioPromedio, setSalarioPromedio] = useState(0)
  const [masaSalarial, setMasaSalarial] = useState(0)
  const [ffep, setFfep] = useState(0)
  const [devengadoCuota, setDevengadoCuota] = useState(0)
  const [totalAPagar, setTotalAPagar] = useState(0)
  const [puedeValidar, setPuedeValidar] = useState(false)
  const [idNomina, setIdNomina] = useState(0)
  const [emptyErrorTable, setEmptyErrorTable] = useState({
    icon: 'error',
    title: 'Aún no cargaste ningún archivo',
    description: '',
    iconStyles: 'text-warning-20',
    titleStyles: 'text-primary-10',
    descriptionStyles: 'w-96 text-balance',
  })

  const [openModalSuccessAfterValidarDatos, setOpenModalSuccessAfterValidarDatos] = useState(false)
  const [errorsValidateNomina, setErrorsValidateNomina] = useState(0)
  const [iconModalValidateNomina, setIconModalValidateNomina] = useState('check_circle')
  const [tituloModalValidateNomina, setTituloModalValidateNomina] = useState('Validaste los datos correctamente')
  const [mensajeModalValidateNomina, setMensajeModalValidateNomina] = useState(
    'Los datos ingresados están siendo analizados por Provincia ART'
  )

  const [tableHeaders, setTableHeaders] = useState({})
  const pageRef = useRef()
  const isFirstLoadRef = useRef(true)
  const isLoadingFromButtonRef = useRef(false)
  const [openModalUpdateDetalleNomina, setOpenModalUpdateDetalleNomina] = useState(false)
  const [openModalEliminarNomina, setOpenModalEliminarNomina] = useState(false)
  const [isLoadingTable, setIsLoadingTable] = useState(false)

  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')

  const handleRedirectToInicio = () => {
    navigate(`/`)
  }

  const [onAcceptModal, setOnAcceptModal] = useState(() => handleRedirectToInicio)

  useEffect(() => {
    const init = async () => {
      if (id) {
        setIsLoadingTable(true)
        setIdNomina(id)
      }
    }
    init()
  }, [id])

  useEffect(() => {
    if (!idNomina) return

    // Evita doble fetch cuando handleButtonCargarNomina ya dispara la carga
    if (isLoadingFromButtonRef.current) {
      isLoadingFromButtonRef.current = false
      isFirstLoadRef.current = false
      return
    }
    if (debouncedSearchValue.length > 3) {
      const { page, pageSize } = { page: 0, pageSize: pageRef?.current?.pageSize ?? 10 }
      handleCargarNomina({ page: page, pageSizeN: pageSize, id: idNomina, filter: debouncedSearchValue })
      isFirstLoadRef.current = false
    } else if (debouncedSearchValue.length === 0) {
      const { page, pageSize } = pageRef.current ?? { page: 0, pageSize: 10 }
      handleCargarNomina({
        page: page,
        pageSizeN: pageSize,
        id: idNomina,
        filter: '',
        isInitialLoad: isFirstLoadRef.current,
      })
      isFirstLoadRef.current = false
    }
  }, [debouncedSearchValue, idNomina])

  // Restaura el idNomina persistido al entrar (o recargar con F5) en la ruta de
  // carga. La limpieza al salir de /cargar-nomina hacia el inicio (`/`) la hace
  // useClearNominaOnHome en ProtectedLayout; aquí sólo se valida que el id
  // guardado pertenezca al período de la ruta, para no reutilizar el id de una
  // nómina vieja (ej: 7870) al cargar una nómina nueva.
  useEffect(() => {
    const regex = /^\/cargar-nomina\/(\d{4}-(0[1-9]|1[0-2]))\/carga$/
    const match = regex.exec(location.pathname)
    if (!match) return

    const periodoRuta = match[1]
    const stored = localStorage.getItem('idNomina')
    let storedData = null
    try {
      storedData = stored ? JSON.parse(stored) : null
    } catch {
      storedData = null
    }
    if (storedData && String(storedData.periodo) === periodoRuta) {
      setIdNomina(storedData.id)
    } else {
      localStorage.removeItem('idNomina')
      setIdNomina(0)
    }
  }, [location.pathname])

  useEffect(() => {
    if (searchValue.length > 3 || searchValue.length === 0) {
      const handler = setTimeout(() => {
        setDebouncedSearchValue(searchValue)
      }, DEBOUNCE_TIME) // 500ms debounce time

      return () => {
        clearTimeout(handler)
      }
    } else {
      setDebouncedSearchValue('')
    }
  }, [searchValue])

  const normalizeEmptyValue = (value) => {
    if (value === null || value === undefined || value === '') return '-'
    return value
  }

  const detalleNominaAdapterToDom = (detalle) => {
    const declaracionesAdaptedToDom = detalle?.rows.map((item) => {
      let flagErrores = null
      if (item.erroresRaw.errores.length) {
        flagErrores = {
          border: null,
          color: 'warning',
          description: item.errores,
          gradient: null,
          icon: 'info',
        }
      }
      const buttonEditaItemDetalleNominaIcon = (
        <IconButton
          key={item.id}
          icon={'edit'}
          data-raw={JSON.stringify(item.erroresRaw)}
          className={`gap-0 m-0 p-0 w-full ${!flagErrores ? 'hidden' : ''}`}
          onClick={async () => {
            setItemDetalleNomina(item)
            setOpenModalCorreccionErrorItem(true)
          }}
        />
      )
      const tipo_doc_show = tiposDocumento.filter((e) => e.id == +item.tip_doc)[0]?.value || ''

      return {
        ...item,
        tipo_doc_show: normalizeEmptyValue(tipo_doc_show),
        nro_doc: normalizeEmptyValue(item.nro_doc),
        cuil: normalizeEmptyValue(item.cuil),
        nombre: normalizeEmptyValue(item.nombre),
        sexo: normalizeEmptyValue(item.sexo),
        cant_dias_trabajados: normalizeEmptyValue(item.cant_dias_trabajados),
        fec_nac: normalizeEmptyValue(item.fec_nac),
        sueldo: normalizeEmptyValue(item.sueldo),
        flag: flagErrores,
        acciones: buttonEditaItemDetalleNominaIcon,
      }
    })

    return declaracionesAdaptedToDom
  }
  const handleButtonCargarNomina = async (file) => {
    try {
      loader.show({})
      const response = await createDeclaracionNominaApi({ periodo, file })

      if (response.status == 200) {
        const { id } = response.data
        isLoadingFromButtonRef.current = true
        // setIsLoadingTable(true)
        setIdNomina(id)
        localStorage.setItem('idNomina', JSON.stringify({ id, periodo }))

        const { page, pageSize } = pageRef.current ?? { page: 0, pageSize: 10 }
        await handleCargarNomina({
          page,
          pageSizeN: pageSize,
          id,
          filter: '',
          isInitialLoad: true,
        })

        loader.hide()
      }
    } catch (error) {
      loader.hide({})
      return handleErrorArchivoCargaNomina(error)
    }
  }

  const handlePagination = ({ page = 1, pageSize = 5, forceFirstPage = false }) => {
    const actualPage = forceFirstPage ? 1 : page
    // page 0 (o negativo) significa primera página: offset 0.
    // Evita query params como offset=-10.
    const offset = Math.max(0, (actualPage - 1) * pageSize)
    return { offset, pageSize }
  }

  const handleCargarNomina = async ({ page, pageSizeN, id, isInitialLoad = false, filter = '' }) => {
    try {
      const queryParams = []
      const { offset, pageSize } = handlePagination({
        page: page,
        pageSize: pageSizeN,
        filters: null,
        forceFirstPage: false,
        searchValue: '',
      })
      queryParams.push(`offset=${offset}`, `limit=${pageSize}`)

      if (filter) {
        queryParams.push(`filter=${encodeURIComponent(filter)}`)
      }
      const query = `?${queryParams.join('&')}`

      setIsLoadingTable(true)
      const response = await getDetalleNominaApi({ id, query })

      if (response.status == 200) {
        const cantErrores = response?.data?.qtyErrores
        const headers = {
          ...response.headers,
          ['x-total-count']: Number(response.headers['x-total-count']),
        }

        setTableHeaders(headers)

        if (cantErrores == 0) {
          setDisabledBtnRevisarNomina(false)
        } else {
          setDisabledBtnRevisarNomina(true)
        }

        let dataAdapted = detalleNominaAdapter(response)
        dataAdapted = detalleNominaAdapterToDom(dataAdapted)
        setDetalleNomina(dataAdapted)
        setCantidadErrores(cantErrores)
        setIdNomina(id)
        if (isInitialLoad) {
          handleErrorCargaNominaButton(response)
        }
        setIsLoadingTable(false)
        loader.hide()
      }
    } catch (error) {
      setIsLoadingTable(false)
      return handleErrorArchivoCargaNomina(error)
    }
  }

  const handleErrorCargaNominaButton = (response) => {
    const hasError = response?.data?.qtyErrores == 0 ? false : true
    setDisabledBtnRevisarNomina(hasError)
    if (hasError) {
      const errores = response.data.qtyErrores
      setCantidadErrores(errores)
      if (errores < 10) {
        alert.show({
          description: `Se encontraron ${errores} errores en los datos cargados. Por favor, editalos para continuar.`,
          icon: 'error',
        })
      } else {
        alert.show({
          description: `Se detectaron ${errores} errores en los datos. Te sugerimos descargar la planilla de errores, corregirla y volver a cargarla.`,
          icon: 'error',
        })
      }
    }
  }

  const getErrorMessage = () => {
    return {
      cuil_duplicacion: {
        icon: 'cancel',
        iconStyles: 'text-error-20',
        title: 'CUIL duplicado',
        description: 'La planilla no puede contener CUIL repetidos. Corregilo e intentá de nuevo',
      },
      formato_incorrecto: {
        icon: 'cancel',
        iconStyles: 'text-error-20',
        title: 'Formato de planilla incorrecto',
        description:
          'El archivo cargado no corresponde al formato esperado. Para continuar, descargá y utilizá la planilla base.',
      },
      extension_invalida: {
        icon: 'cancel',
        iconStyles: 'text-error-20',
        title: 'Extensión de archivo no válida',
        description: 'La planilla debe estar en formato .xlsx para poder cargarse correctamente.',
      },
      tamaño_excedido: {
        icon: 'cancel',
        iconStyles: 'text-error-20',
        title: 'Tamaño de archivo excedido',
        description: 'La planilla no puede superar los 10 MB. Reduci el tamaño e intentá nuevamente',
      },
    }
  }

  const handleErrorArchivoCargaNomina = (response) => {
    //Manejo de errores desde el servicio: Ej: cod-error - message?
    if (response.response?.data?.type == 'InputValidation') {
      const message = response.response.data.message[0]
      let attr = {}
      if (message.includes('.xlsx')) {
        attr = getErrorMessage().extension_invalida
      }
      if (message.includes('columnas')) {
        attr = getErrorMessage().formato_incorrecto
      }
      if (message.includes('duplicado')) {
        attr = getErrorMessage().cuil_duplicacion
      }
      if (message.includes('superar')) {
        attr = getErrorMessage().tamaño_excedido
      }
      setEmptyErrorTable((prev) => ({
        ...prev,
        ...attr,
      }))
      setDetalleNomina([])
    }
  }
  const handleButtonGetPlantillaBase = async () => {
    //efectuar loading hhtp requests
    try {
      await getPlantilaBaseApi()
    } catch (error) {
      //manage errors
    }
  }
  const handleButtonGetNominaConErrores = async () => {
    //efectuar loading hhtp requests
    try {
      await getNominaConErroresApi(idNomina)
    } catch (error) {
      //manage errors
    }
  }
  const getResumenNomina = async ({ id }) => {
    const response = await getResumenNominaApi(id)
    const resumen = resumenNominaAdapter(response)
    setEmpleados(resumen?.empleados)
    setSalarioPromedio(resumen?.salario_promedio)
    setMasaSalarial(resumen?.masa_salarial)
    setFfep(resumen?.ffep)
    setDevengadoCuota(resumen?.devengado_cuota)
    setTotalAPagar(resumen?.total_a_pagar)
    setPuedeValidar(resumen?.puede_validar)
  }

  const handleModalRevisarNomina = async () => {
    await getResumenNomina({ id: idNomina })
    setOpenModalValidarDatos(true)
  }

  const handleValidateNomina = async () => {
    try {
      loader.show({ message: 'Validando nómina' })
      const response = await validateNominaApi({ id: idNomina })
      if (response.status == 200) {
        loader.hide()
        setIconModalValidateNomina('check_circle')
        setTituloModalValidateNomina('Validaste los datos correctamente')
        setMensajeModalValidateNomina('Los datos ingresados están siendo analizados por Provincia ART')
        setOnAcceptModal(() => () => handleRedirectToInicio())
        setOpenModalValidarDatos(false)
        setOpenModalSuccessAfterValidarDatos(true)
      } else {
        loader.hide()
        handleErrorValidateNomina()
      }
    } catch (error) {
      loader.hide()
      handleErrorValidateNomina()
    }
  }
  const handleEliminarNomina = async () => {
    try {
      const response = await bajaDeclaracion({ idNomina: idNomina })
      if (response.status == 200) {
        handleRedirectToInicio()
      }
    } catch (error) {
      alert.show({
        title: `Ha ocurrido un errror al eliminar nómina.`,
      })
    }
  }

  const handleErrorValidateNomina = () => {
    setErrorsValidateNomina((prev) => prev + 1)
    if (errorsValidateNomina == QUANTITY_LIMIT_ERROR) {
      setErrorsValidateNomina(0)
      setIconModalValidateNomina('info')
      setTituloModalValidateNomina('No pudimos completar la validación')
      setMensajeModalValidateNomina(
        'El inconveniente persiste tras varios intentos. Para mayor información, podes contactarte a emision@provart.com.ar'
      )
    } else {
      setIconModalValidateNomina('info')
      setTituloModalValidateNomina('No pudimos completar la validación')
      setMensajeModalValidateNomina(
        'El inconveniente persiste tras varios intentos. Por favor intentá nuevamente en unos minutos'
      )
    }
    setOpenModalSuccessAfterValidarDatos(true)
    setOnAcceptModal(() => () => setOpenModalSuccessAfterValidarDatos(false))
  }

  const onCloseModalRevisarNomina = () => {
    setOpenModalValidarDatos(false)
  }

  const onCloseModalCorreccionErrorItem = () => {
    setOpenModalCorreccionErrorItem(false)
  }

  /**
   * Cunado se actualiza un item de la tabla
   */
  const handleUpdateItemDetalleNomina = async (data) => {
    try {
      const response = await updateValuesItemDetalleNominaApi({
        id: data.idNomina,
        idd: data.idDetalleNomina,
        dtoItemDetalle: data.dtoItemDetalle,
      })

      if (response.status == 200) {
        alert.show({
          title: '',
          description: `Los cambios fueron guardados con éxito.`,
          variant: 'success',
          icon: 'check_circle',
        })
        setOpenModalCorreccionErrorItem(false)
        const { page, pageSize } = pageRef?.current ?? { page: 0, pageSize: 10 }
        handlePageChange({ page, pageSize, force: true })
      }
    } catch (error) {
      return handleErrorUpdateItemDetalleNomina(error)
    }
  }

  const handleErrorUpdateItemDetalleNomina = (error) => {
    return error
  }

  const handlePageChange = async ({ page, pageSize, force = false, reload = false }) => {
    if (reload) {
      await handleCargarNomina({
        page,
        pageSizeN: pageSize,
        id: idNomina,
        filter: searchValue,
        isInitialLoad: true,
      })
    }
    if (force) {
      await handleCargarNomina({
        page,
        pageSizeN: pageSize,
        id: idNomina,
        filter: searchValue,
      })
    }
    if (pageRef.current?.page === page && pageRef.current?.pageSize === pageSize) return
    pageRef.current = { page, pageSize }
    await handleCargarNomina({
      page,
      pageSizeN: pageSize,
      id: idNomina,
      isInitialLoad: false,
      filter: searchValue,
    })
  }

  const handleClick = () => {
    if (idNomina) {
      setOpenModalUpdateDetalleNomina(true)
    } else {
      // llamar de forma segura
      if (fileRef.current?.openFilePicker) fileRef.current.openFilePicker()
      else console.warn('openFilePicker no disponible aún', fileRef.current)
    }
  }

  const handleConfirm = () => {
    setOpenModalUpdateDetalleNomina(false)
    fileRef.current?.openFilePicker?.()
  }

  /*
   * Cuando ya hay un detalle de nomina cargada y se desea remplazar el contenido
   */
  const handleUpdateDetalleNomina = async (file) => {
    try {
      loader.show({})
      const response = await updateDeclaracionNominaApi({ idNomina, file })

      if (response.status == 200) {
        handlePageChange({ page: 0, pageSize: 10, force: true, reload: true })
        // loader.hide()
      }
    } catch (error) {
      loader.hide({})
      return handleErrorArchivoCargaNomina(error)
    }
  }
  return (
    <div className="w-full hidden sm:flex flex-col">
      <div className="w-full px-8 lg:px-24 mt-4">
        <BackButton />
      </div>
      <div className="w-full px-10 lg:px-24 mt-2 pb-[62px]">
        <IndicadorOs razonSocial={razonSocial} cuit={cuit} periodo={periodo} />
        <div className="flex w-full pb-5 mt-5">
          <div className="flex w-full  items-center justify-between ">
            <div className="flex justify-start">
              <SearchBar
                placeholder="Buscar por N° de documento, CUIL o Nombre"
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                opciones={[]}
                maxOpciones={0}
              />
            </div>
            <div className="flex justify-end">
              <TextButton IconLeft="download" size="small" className="mr-1" onClick={handleButtonGetPlantillaBase}>
                Descargar planilla base
              </TextButton>
              {/* <ButtonCargaNomina onFileSelected={handleButtonCargarNomina}></ButtonCargaNomina> */}
              <ButtonCargaNomina
                ref={fileRef}
                onFileSelected={isFirstLoadRef.current ? handleButtonCargarNomina : handleUpdateDetalleNomina}
              />

              <OutlineButton
                IconLeft="add"
                size="small"
                variant="default"
                className="text-primary-10 ml-4 mt-2"
                onClick={handleClick}
              >
                Cargar archivo
              </OutlineButton>
            </div>
          </div>
        </div>
        <section id="table" className="flex flex-col gap-4 pb-4">
          <div className=" relative -ml-5 ">
            <PaginatedLoadingTable
              data={detalleNomina}
              showPagination={true}
              columns={columnasDetalleNomina}
              onPageChange={handlePageChange}
              headers={tableHeaders}
              isLoading={false}
              emptyState={!isLoadingTable ? emptyErrorTable : null}
              selectable={false}
              selected={selectedRow}
              flags
              uniqueKey="uuid"
              // loadingIcon="progress_activity"
              // loadingText="Solicitud en proceso"
              // loadingIconVariant="primary"
            />
          </div>
        </section>
        <section id="buttons">
          <div className="flex place-items-center mt-4">
            <Badge
              count={cantidadErrores}
              className={`${(idNomina === 0 || cantidadErrores === 0) && 'hidden'}`}
              variant="error"
            >
              Errores:
            </Badge>
            <div className="flex gap-3 justify-end ml-auto">
              <PrimaryButton
                IconLeft="download"
                size="small"
                className={`mr-1 ${idNomina === 0 ? 'hidden' : ''}`}
                disabled={cantidadErrores == 0 ? true : false}
                onClick={handleButtonGetNominaConErrores}
              >
                Descargar planilla con errores
              </PrimaryButton>
              <PrimaryButton
                className={`${idNomina === 0 && 'hidden'}`}
                size="small"
                disabled={disabledBtnRevisarNomina}
                onClick={handleModalRevisarNomina}
              >
                Siguiente
              </PrimaryButton>
            </div>
          </div>
        </section>

        <ModalValidarDatos
          openModal={openModalValidarDatos}
          onCloseModal={onCloseModalRevisarNomina}
          titulo={'Resumen de los datos registrados'}
          periodo={adaptarPeriodo(periodo)}
          empleados={empleados}
          salario_promedio={salarioPromedio}
          masa_salarial={masaSalarial}
          ffep={ffep}
          devengado_cuota={devengadoCuota}
          total_a_pagar={totalAPagar}
          puede_validar={puedeValidar}
          onAcceptModal={handleValidateNomina}
          onEliminarModal={() => setOpenModalEliminarNomina(true)}
        ></ModalValidarDatos>

        <ModalSuccessAfterValidarDatos
          icon={iconModalValidateNomina}
          titulo={tituloModalValidateNomina}
          mensaje={mensajeModalValidateNomina}
          openModal={openModalSuccessAfterValidarDatos}
          onCloseModal={() => setOpenModalSuccessAfterValidarDatos(false)}
          onAcceptModal={onAcceptModal}
        ></ModalSuccessAfterValidarDatos>

        <ModalUpdateDetalleNomina
          icon={'info'}
          titulo={'Reemplazar archivo'}
          mensaje={'Si subís un nuevo archivo, la información cargada se borrará y será reemplazada por la nueva'}
          openModal={openModalUpdateDetalleNomina}
          onCloseModal={() => setOpenModalUpdateDetalleNomina(false)}
          onAcceptModal={() => handleConfirm()}
        ></ModalUpdateDetalleNomina>

        <ModalEliminarNomina
          icon={'info'}
          titulo={'Eliminar la nómina'}
          mensaje={'Si la eliminás, se perderán los datos cargados hasta el momento.'}
          openModal={openModalEliminarNomina}
          onCloseModal={() => setOpenModalEliminarNomina(false)}
          onAcceptModal={() => handleEliminarNomina()}
        ></ModalEliminarNomina>

        {openModalCorreccionErrorItem && (
          <ModalCorreccionErrorItem
            periodo={periodo}
            openModal={openModalCorreccionErrorItem}
            onCloseModal={onCloseModalCorreccionErrorItem}
            titulo="Editar trabajador"
            itemDetalleNomina={itemDetalleNomina}
            onAcceptModal={handleUpdateItemDetalleNomina}
          ></ModalCorreccionErrorItem>
        )}
      </div>
    </div>
  )
}
